// frontend/src/pages/TermsOfService.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguageStore } from '../store/useLanguageStore';
import { FileText, ArrowLeft, Mail } from 'lucide-react';
import logoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import logoSquare from '../assets/images/datiaFlow_cuadrado.jpg';

/* Anti-scraping email render — bots leen el source, no ejecutan JS */
const SafeEmail = ({ u, d }) => {
  const addr = `${u}\u0040${d}`;
  return <a href={`mai${''}lto:${addr}`} className="text-blue-600 hover:underline">{addr}</a>;
};

/* ═══════════════════════════════════════════════════
   v2 — 2026-07-29: Auditoría legal pre-abogado.
   Cambios: §9 renombrado + sub-sección c) Derecho al Olvido.
   ═══════════════════════════════════════════════════ */
const LAST_UPDATED = '2026-07-29';

const CONTENT = {
  ES: {
    title: 'Términos de Servicio',
    lastUpdated: `Última actualización: ${LAST_UPDATED}`,
    backToHome: 'Volver al inicio',
    sections: [
      {
        heading: '1. Definición del servicio',
        body: `DatiaFlow es una plataforma SaaS (Software como Servicio) de extracción, transformación y carga de datos (ETL/ELT) desarrollada y operada por Varanext, con sede en Lima, Perú.

El servicio permite a los usuarios configurar y ejecutar pipelines de migración y sincronización de datos entre múltiples sistemas (bases de datos, aplicaciones SaaS, archivos planos y servicios en la nube), utilizando una arquitectura de Zero Data Retention (ZDR) donde los datos de los clientes se procesan exclusivamente en memoria RAM y no se almacenan en disco en los servidores de Varanext.

Excepción documentada: cuando el destino de una migración es un archivo plano (CSV, Excel, DBF), el archivo resultante se almacena temporalmente en AWS S3 por un máximo de 7 días.

Al registrarse en DatiaFlow o utilizar el servicio, usted acepta estos Términos de Servicio en su totalidad.`
      },
      {
        heading: '2. Cuentas y acceso',
        body: `Para utilizar DatiaFlow, usted debe crear una cuenta proporcionando información veraz y actualizada. Usted es responsable de:

— Mantener la confidencialidad de sus credenciales de acceso (email y contraseña).
— Todas las actividades que ocurran bajo su cuenta.
— Notificar a Varanext de cualquier uso no autorizado de su cuenta enviando un correo a support@varanext.com.

DatiaFlow aplica una política de sesión única: al iniciar sesión en un dispositivo, las sesiones previas se invalidan automáticamente.

Varanext se reserva el derecho de suspender o cancelar cuentas que violen estos términos o que muestren actividad sospechosa o fraudulenta.`
      },
      {
        heading: '3. Uso aceptable',
        body: `Usted se compromete a utilizar DatiaFlow exclusivamente para fines legítimos y de acuerdo con estos términos. Queda expresamente prohibido:

— Utilizar el servicio para migrar, almacenar o procesar datos obtenidos de forma ilegal o sin las autorizaciones necesarias.
— Intentar acceder a datos de otros clientes o comprometer la seguridad de la plataforma.
— Realizar ingeniería inversa, descompilar o intentar extraer el código fuente de DatiaFlow.
— Utilizar el servicio de manera que genere una carga excesiva o desproporcionada en la infraestructura.
— Revender, sublicenciar o redistribuir el servicio sin autorización escrita de Varanext.
— Proporcionar credenciales falsas o suplantadas a los conectores de la plataforma.

El incumplimiento de estas obligaciones puede resultar en la suspensión inmediata del servicio.`
      },
      {
        heading: '4. Credenciales de conexión y responsabilidad del cliente',
        body: `DatiaFlow permite configurar conexiones a sistemas externos (bases de datos, APIs, servicios SaaS) proporcionando credenciales de acceso. El cliente es responsable de:

— Garantizar que las credenciales proporcionadas son válidas y que el cliente tiene autorización para acceder a los sistemas de origen y destino.
— Asegurar que los datos migrados cumplen con la normativa de protección de datos aplicable en su jurisdicción.
— Configurar los permisos mínimos necesarios en los sistemas de origen y destino (principio de mínimo privilegio).

Varanext cifra todas las credenciales de conexión con Fernet JIT y nunca las almacena en texto plano. Sin embargo, Varanext no es responsable de las consecuencias derivadas de credenciales con permisos excesivos proporcionadas por el cliente.`
      },
      {
        heading: '5. Planes, quotas y facturación',
        body: `DatiaFlow ofrece diferentes planes de suscripción con quotas configurables de registros por ejecución y MB transferidos. Los detalles específicos de cada plan están disponibles en la plataforma.

— Plan Trial/Freemium: acceso limitado con quotas reducidas para evaluación.
— Planes comerciales: quotas ampliadas según el plan contratado.
— Superación de quotas: cuando un pipeline supera la quota de registros o MB, la ejecución finaliza con estado PARTIAL_SUCCESS (éxito parcial). No se produce un error completo.

Los precios y modalidades de facturación se comunicarán al momento de la contratación. Varanext se reserva el derecho de modificar los precios con 30 días de anticipación para planes activos.`
      },
      {
        heading: '6. Disponibilidad del servicio',
        body: `Varanext se compromete a realizar esfuerzos razonables para mantener DatiaFlow disponible de forma continua ("best effort"). Sin embargo, Varanext no garantiza una disponibilidad del 100% y no se hace responsable por:

— Interrupciones programadas para mantenimiento (notificadas con antelación razonable).
— Interrupciones causadas por fuerza mayor, fallos de proveedores de infraestructura (AWS), ataques de terceros, o circunstancias fuera del control razonable de Varanext.
— Fallos en los sistemas de origen o destino del cliente.

Varanext tiene como objetivo interno mantener una disponibilidad superior al 99.5%, pero esto no constituye un SLA (Service Level Agreement) formal vinculante. En el futuro, Varanext podrá ofrecer SLAs formales para planes enterprise.`
      },
      {
        heading: '7. Propiedad intelectual',
        body: `a) Software: todo el código fuente, diseño, arquitectura, algoritmos, interfaces y documentación de DatiaFlow son propiedad exclusiva de Varanext y están protegidos por las leyes de propiedad intelectual aplicables. La suscripción otorga al cliente una licencia de uso limitada, no exclusiva, no transferible y revocable.

b) Datos del cliente: los datos que el cliente migra, transforma o sincroniza a través de DatiaFlow son y permanecen propiedad exclusiva del cliente. Varanext no adquiere ningún derecho sobre los datos del cliente y los procesa exclusivamente según las instrucciones definidas en la configuración de los pipelines.

c) Configuraciones y mappings: las configuraciones de pipelines, mapeos de campos y reglas de transformación creadas por el cliente en la plataforma son propiedad del cliente. El cliente puede exportarlas en cualquier momento.`
      },
      {
        heading: '8. Limitación de responsabilidad',
        body: `EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY APLICABLE:

— La responsabilidad total de Varanext frente al cliente por cualquier reclamación derivada de estos términos o del uso del servicio no excederá el monto total pagado por el cliente a Varanext durante los 12 meses anteriores al evento que dio origen a la reclamación.

— Varanext no será responsable por daños indirectos, incidentales, especiales, consecuenciales o punitivos, incluyendo pérdida de datos del cliente, pérdida de beneficios, interrupción de negocio, o costos de adquisición de servicios sustitutos.

— Varanext no es responsable de la pérdida o corrupción de datos en los sistemas de origen o destino del cliente. Se recomienda encarecidamente que el cliente mantenga copias de respaldo de sus datos antes de ejecutar migraciones.

— Dado que DatiaFlow opera bajo la arquitectura Zero Data Retention, los datos de los clientes no se almacenan en los servidores de Varanext y no pueden ser recuperados por Varanext en caso de pérdida.`
      },
      {
        heading: '9. Suspensión, cancelación y eliminación de datos',
        body: `a) Cancelación por el cliente: el cliente puede cancelar su suscripción en cualquier momento contactando a support@varanext.com. Tras la cancelación:
— Los datos de configuración (pipelines, mappings) se conservan durante 30 días para permitir la reactivación.
— Pasados los 30 días, los datos de configuración se eliminan permanentemente.
— Los datos procesados en tránsito no se ven afectados (ZDR — no hay datos almacenados).

b) Suspensión por Varanext: Varanext puede suspender o cancelar el acceso del cliente en caso de:
— Incumplimiento de estos Términos de Servicio.
— Falta de pago (cuando aplique).
— Actividad sospechosa, fraudulenta o que comprometa la seguridad de la plataforma.
— Requerimiento de autoridad competente.

En caso de suspensión, Varanext notificará al cliente por correo electrónico con la razón de la suspensión, salvo en casos de urgencia por seguridad.

c) Solicitud de eliminación de datos (Derecho al Olvido): el cliente puede solicitar la eliminación completa de su cuenta y todos los datos asociados en cualquier momento, independientemente de si desea cancelar su suscripción. El proceso es el siguiente:
— El usuario dispone de una opción para la eliminación de datos desde la plataforma.
— La plataforma envía un correo de confirmación con un enlace de verificación válido por 48 horas.
— Tras la confirmación, se inicia un período de gracia de 30 días durante el cual la solicitud puede ser cancelada.
— Transcurrido el período de gracia, la eliminación es irreversible. Los datos del cliente se anonimizan para cumplir con las obligaciones de accountability de Varanext.
— Para más información sobre el proceso y los datos afectados, consulte nuestra Política de Privacidad.`
      },
      {
        heading: '10. Indemnización',
        body: `El cliente se compromete a indemnizar y mantener indemne a Varanext, sus directores, empleados y contratistas de cualquier reclamación, demanda, responsabilidad, daño, costo o gasto (incluyendo honorarios legales razonables) derivados de:

— El uso del servicio por parte del cliente en violación de estos términos.
— La violación de derechos de terceros por parte del cliente a través del uso del servicio.
— Los datos proporcionados o procesados por el cliente a través de la plataforma.`
      },
      {
        heading: '11. Modificaciones a estos términos',
        body: `Varanext se reserva el derecho de modificar estos Términos de Servicio. En caso de cambios sustanciales, le notificaremos por correo electrónico con al menos 30 días de anticipación. El uso continuado del servicio después de la entrada en vigor de los cambios constituirá la aceptación de los nuevos términos.

Si no está de acuerdo con los cambios, puede cancelar su suscripción antes de que entren en vigor.`
      },
      {
        heading: '12. Ley aplicable y jurisdicción',
        body: `Estos Términos de Servicio se rigen por las leyes de la República del Perú.

Cualquier controversia derivada de estos términos o del uso del servicio será sometida a la jurisdicción exclusiva de los juzgados y tribunales del distrito judicial de Lima, Perú, renunciando las partes a cualquier otro fuero que pudiera corresponderles.

Para usuarios de la Unión Europea: las disposiciones obligatorias de protección al consumidor de su país de residencia permanecerán aplicables en la medida que la ley lo requiera.`
      },
      {
        heading: '13. Divisibilidad',
        body: `Si alguna disposición de estos Términos de Servicio fuera declarada inválida o inaplicable por un tribunal competente, las disposiciones restantes permanecerán en pleno vigor y efecto.`
      },
      {
        heading: '14. Contacto',
        body: `Para cualquier consulta relacionada con estos Términos de Servicio, puede contactarnos en:

Varanext
Lima, Perú
support@varanext.com`
      }
    ],
    privacyPolicyLink: 'Política de Privacidad',
    cookiePolicyLink: 'Política de Cookies'
  },

  EN: {
    title: 'Terms of Service',
    lastUpdated: `Last updated: ${LAST_UPDATED}`,
    backToHome: 'Back to home',
    sections: [
      {
        heading: '1. Service Definition',
        body: `DatiaFlow is a SaaS (Software as a Service) platform for data extraction, transformation, and loading (ETL/ELT) developed and operated by Varanext, headquartered in Lima, Peru.

The service allows users to configure and execute data migration and synchronization pipelines between multiple systems (databases, SaaS applications, flat files, and cloud services), using a Zero Data Retention (ZDR) architecture where client data is processed exclusively in RAM and is not stored on disk on Varanext servers.

Documented exception: when a migration target is a flat file (CSV, Excel, DBF), the resulting file is temporarily stored in AWS S3 for a maximum of 7 days.

By registering on DatiaFlow or using the service, you accept these Terms of Service in their entirety.`
      },
      {
        heading: '2. Accounts and Access',
        body: `To use DatiaFlow, you must create an account providing truthful and up-to-date information. You are responsible for:

— Maintaining the confidentiality of your login credentials (email and password).
— All activities that occur under your account.
— Notifying Varanext of any unauthorized use of your account by emailing support@varanext.com.

DatiaFlow enforces a single-session policy: when you log in on a new device, previous sessions are automatically invalidated.

Varanext reserves the right to suspend or cancel accounts that violate these terms or that show suspicious or fraudulent activity.`
      },
      {
        heading: '3. Acceptable Use',
        body: `You agree to use DatiaFlow exclusively for legitimate purposes and in accordance with these terms. The following is expressly prohibited:

— Using the service to migrate, store, or process data obtained illegally or without the necessary authorizations.
— Attempting to access other clients' data or compromising the security of the platform.
— Reverse engineering, decompiling, or attempting to extract DatiaFlow's source code.
— Using the service in a way that generates excessive or disproportionate load on the infrastructure.
— Reselling, sublicensing, or redistributing the service without written authorization from Varanext.
— Providing false or spoofed credentials to the platform's connectors.

Failure to comply with these obligations may result in immediate suspension of the service.`
      },
      {
        heading: '4. Connection Credentials and Client Responsibility',
        body: `DatiaFlow allows configuring connections to external systems (databases, APIs, SaaS services) by providing access credentials. The client is responsible for:

— Ensuring that the provided credentials are valid and that the client has authorization to access the source and target systems.
— Ensuring that migrated data complies with applicable data protection regulations in their jurisdiction.
— Configuring the minimum necessary permissions on source and target systems (principle of least privilege).

Varanext encrypts all connection credentials with Fernet JIT and never stores them in plain text. However, Varanext is not responsible for the consequences arising from credentials with excessive permissions provided by the client.`
      },
      {
        heading: '5. Plans, Quotas, and Billing',
        body: `DatiaFlow offers different subscription plans with configurable quotas for records per execution and MB transferred. The specific details of each plan are available on the platform.

— Trial/Freemium plan: limited access with reduced quotas for evaluation.
— Commercial plans: expanded quotas according to the contracted plan.
— Quota exceeded: when a pipeline exceeds the record or MB quota, the execution finishes with PARTIAL_SUCCESS status. A complete failure does not occur.

Prices and billing modalities will be communicated at the time of contracting. Varanext reserves the right to modify prices with 30 days' notice for active plans.`
      },
      {
        heading: '6. Service Availability',
        body: `Varanext commits to making reasonable efforts to keep DatiaFlow continuously available ("best effort"). However, Varanext does not guarantee 100% availability and is not responsible for:

— Scheduled maintenance interruptions (notified with reasonable advance notice).
— Interruptions caused by force majeure, infrastructure provider failures (AWS), third-party attacks, or circumstances beyond Varanext's reasonable control.
— Failures in the client's source or target systems.

Varanext's internal target is to maintain availability above 99.5%, but this does not constitute a formal binding SLA (Service Level Agreement). In the future, Varanext may offer formal SLAs for enterprise plans.`
      },
      {
        heading: '7. Intellectual Property',
        body: `a) Software: all source code, design, architecture, algorithms, interfaces, and documentation of DatiaFlow are the exclusive property of Varanext and are protected by applicable intellectual property laws. The subscription grants the client a limited, non-exclusive, non-transferable, and revocable license to use.

b) Client data: data that the client migrates, transforms, or synchronizes through DatiaFlow is and remains the exclusive property of the client. Varanext does not acquire any rights over client data and processes it exclusively according to the instructions defined in the pipeline configuration.

c) Configurations and mappings: pipeline configurations, field mappings, and transformation rules created by the client on the platform are the property of the client. The client may export them at any time.`
      },
      {
        heading: '8. Limitation of Liability',
        body: `TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:

— Varanext's total liability to the client for any claim arising from these terms or the use of the service shall not exceed the total amount paid by the client to Varanext during the 12 months prior to the event giving rise to the claim.

— Varanext shall not be liable for indirect, incidental, special, consequential, or punitive damages, including loss of client data, loss of profits, business interruption, or costs of acquiring substitute services.

— Varanext is not responsible for the loss or corruption of data in the client's source or target systems. It is strongly recommended that the client maintain backup copies of their data before executing migrations.

— Since DatiaFlow operates under the Zero Data Retention architecture, client data is not stored on Varanext servers and cannot be recovered by Varanext in case of loss.`
      },
      {
        heading: '9. Suspension, Cancellation, and Data Deletion',
        body: `a) Cancellation by the client: the client may cancel their subscription at any time by contacting support@varanext.com. After cancellation:
— Configuration data (pipelines, mappings) is retained for 30 days to allow reactivation.
— After 30 days, configuration data is permanently deleted.
— Data processed in transit is not affected (ZDR — no data stored).

b) Suspension by Varanext: Varanext may suspend or cancel the client's access in the following cases:
— Violation of these Terms of Service.
— Non-payment (when applicable).
— Suspicious, fraudulent activity or activity that compromises platform security.
— Request from a competent authority.

In the event of suspension, Varanext will notify the client by email with the reason for the suspension, except in cases of security urgency.

c) Data Deletion Request (Right to be Forgotten): the client may request the complete deletion of their account and all associated data at any time, regardless of whether they wish to cancel their subscription. The process is as follows:
— The user requests deletion from the platform or by sending an email to support@varanext.com.
— A confirmation email is sent with a verification link valid for 48 hours.
— After confirmation, a 30-day grace period begins during which the request can be cancelled.
— Once the grace period expires, deletion is irreversible. Client data is anonymized to comply with Varanext's accountability obligations.
— For more information about the process and the data affected, please see our Privacy Policy.`
      },
      {
        heading: '10. Indemnification',
        body: `The client agrees to indemnify and hold harmless Varanext, its directors, employees, and contractors from any claim, demand, liability, damage, cost, or expense (including reasonable legal fees) arising from:

— The client's use of the service in violation of these terms.
— The client's violation of third-party rights through the use of the service.
— Data provided or processed by the client through the platform.`
      },
      {
        heading: '11. Changes to These Terms',
        body: `Varanext reserves the right to modify these Terms of Service. In the event of substantial changes, we will notify you by email at least 30 days in advance. Continued use of the service after the changes take effect constitutes acceptance of the new terms.

If you do not agree with the changes, you may cancel your subscription before they take effect.`
      },
      {
        heading: '12. Governing Law and Jurisdiction',
        body: `These Terms of Service are governed by the laws of the Republic of Peru.

Any dispute arising from these terms or the use of the service shall be submitted to the exclusive jurisdiction of the courts of the judicial district of Lima, Peru, with the parties waiving any other jurisdiction that may correspond to them.

For European Union users: mandatory consumer protection provisions of your country of residence will remain applicable to the extent required by law.`
      },
      {
        heading: '13. Severability',
        body: `If any provision of these Terms of Service is declared invalid or unenforceable by a competent court, the remaining provisions shall remain in full force and effect.`
      },
      {
        heading: '14. Contact',
        body: `For any inquiry related to these Terms of Service, you may contact us at:

Varanext
Lima, Peru
support@varanext.com`
      }
    ],
    privacyPolicyLink: 'Privacy Policy',
    cookiePolicyLink: 'Cookie Policy'
  },

  PT: {
    title: 'Termos de Serviço',
    lastUpdated: `Última atualização: ${LAST_UPDATED}`,
    backToHome: 'Voltar ao início',
    sections: [
      {
        heading: '1. Definição do serviço',
        body: `O DatiaFlow é uma plataforma SaaS (Software como Serviço) de extração, transformação e carga de dados (ETL/ELT) desenvolvida e operada pela Varanext, com sede em Lima, Peru.

O serviço permite aos usuários configurar e executar pipelines de migração e sincronização de dados entre múltiplos sistemas (bancos de dados, aplicações SaaS, arquivos planos e serviços em nuvem), utilizando uma arquitetura de Zero Data Retention (ZDR) onde os dados dos clientes são processados exclusivamente em memória RAM e não são armazenados em disco nos servidores da Varanext.

Exceção documentada: quando o destino de uma migração é um arquivo plano (CSV, Excel, DBF), o arquivo resultante é armazenado temporariamente no AWS S3 por no máximo 7 dias.

Ao se registrar no DatiaFlow ou utilizar o serviço, você aceita estes Termos de Serviço em sua totalidade.`
      },
      {
        heading: '2. Contas e acesso',
        body: `Para utilizar o DatiaFlow, você deve criar uma conta fornecendo informações verídicas e atualizadas. Você é responsável por:

— Manter a confidencialidade das suas credenciais de acesso (e-mail e senha).
— Todas as atividades que ocorram sob sua conta.
— Notificar a Varanext de qualquer uso não autorizado da sua conta enviando um e-mail para support@varanext.com.

O DatiaFlow aplica uma política de sessão única: ao fazer login em um dispositivo, as sessões anteriores são automaticamente invalidadas.

A Varanext reserva-se o direito de suspender ou cancelar contas que violem estes termos ou que apresentem atividade suspeita ou fraudulenta.`
      },
      {
        heading: '3. Uso aceitável',
        body: `Você se compromete a utilizar o DatiaFlow exclusivamente para fins legítimos e de acordo com estes termos. Fica expressamente proibido:

— Utilizar o serviço para migrar, armazenar ou processar dados obtidos de forma ilegal ou sem as autorizações necessárias.
— Tentar acessar dados de outros clientes ou comprometer a segurança da plataforma.
— Realizar engenharia reversa, descompilar ou tentar extrair o código-fonte do DatiaFlow.
— Utilizar o serviço de maneira que gere uma carga excessiva ou desproporcional na infraestrutura.
— Revender, sublicenciar ou redistribuir o serviço sem autorização escrita da Varanext.
— Fornecer credenciais falsas ou usurpadas aos conectores da plataforma.

O descumprimento dessas obrigações pode resultar na suspensão imediata do serviço.`
      },
      {
        heading: '4. Credenciais de conexão e responsabilidade do cliente',
        body: `O DatiaFlow permite configurar conexões a sistemas externos (bancos de dados, APIs, serviços SaaS) fornecendo credenciais de acesso. O cliente é responsável por:

— Garantir que as credenciais fornecidas são válidas e que o cliente tem autorização para acessar os sistemas de origem e destino.
— Assegurar que os dados migrados cumprem a normativa de proteção de dados aplicável em sua jurisdição.
— Configurar as permissões mínimas necessárias nos sistemas de origem e destino (princípio do mínimo privilégio).

A Varanext criptografa todas as credenciais de conexão com Fernet JIT e nunca as armazena em texto simples. No entanto, a Varanext não é responsável pelas consequências derivadas de credenciais com permissões excessivas fornecidas pelo cliente.`
      },
      {
        heading: '5. Planos, cotas e faturamento',
        body: `O DatiaFlow oferece diferentes planos de assinatura com cotas configuráveis de registros por execução e MB transferidos. Os detalhes específicos de cada plano estão disponíveis na plataforma.

— Plano Trial/Freemium: acesso limitado com cotas reduzidas para avaliação.
— Planos comerciais: cotas ampliadas conforme o plano contratado.
— Superação de cotas: quando um pipeline supera a cota de registros ou MB, a execução finaliza com status PARTIAL_SUCCESS (sucesso parcial). Não ocorre um erro completo.

Os preços e modalidades de faturamento serão comunicados no momento da contratação. A Varanext reserva-se o direito de modificar os preços com 30 dias de antecedência para planos ativos.`
      },
      {
        heading: '6. Disponibilidade do serviço',
        body: `A Varanext compromete-se a realizar esforços razoáveis para manter o DatiaFlow disponível de forma contínua ("best effort"). No entanto, a Varanext não garante disponibilidade de 100% e não se responsabiliza por:

— Interrupções programadas para manutenção (notificadas com antecedência razoável).
— Interrupções causadas por força maior, falhas de provedores de infraestrutura (AWS), ataques de terceiros ou circunstâncias fora do controle razoável da Varanext.
— Falhas nos sistemas de origem ou destino do cliente.

A Varanext tem como objetivo interno manter disponibilidade superior a 99,5%, mas isso não constitui um SLA (Service Level Agreement) formal vinculante. No futuro, a Varanext poderá oferecer SLAs formais para planos enterprise.`
      },
      {
        heading: '7. Propriedade intelectual',
        body: `a) Software: todo o código-fonte, design, arquitetura, algoritmos, interfaces e documentação do DatiaFlow são propriedade exclusiva da Varanext e estão protegidos pelas leis de propriedade intelectual aplicáveis. A assinatura concede ao cliente uma licença de uso limitada, não exclusiva, intransferível e revogável.

b) Dados do cliente: os dados que o cliente migra, transforma ou sincroniza através do DatiaFlow são e permanecem propriedade exclusiva do cliente. A Varanext não adquire nenhum direito sobre os dados do cliente e os processa exclusivamente segundo as instruções definidas na configuração dos pipelines.

c) Configurações e mapeamentos: as configurações de pipelines, mapeamentos de campos e regras de transformação criadas pelo cliente na plataforma são propriedade do cliente. O cliente pode exportá-las a qualquer momento.`
      },
      {
        heading: '8. Limitação de responsabilidade',
        body: `NA MÁXIMA MEDIDA PERMITIDA PELA LEI APLICÁVEL:

— A responsabilidade total da Varanext perante o cliente por qualquer reclamação derivada destes termos ou do uso do serviço não excederá o valor total pago pelo cliente à Varanext durante os 12 meses anteriores ao evento que deu origem à reclamação.

— A Varanext não será responsável por danos indiretos, incidentais, especiais, consequenciais ou punitivos, incluindo perda de dados do cliente, perda de lucros, interrupção de negócio ou custos de aquisição de serviços substitutos.

— A Varanext não é responsável pela perda ou corrupção de dados nos sistemas de origem ou destino do cliente. Recomenda-se fortemente que o cliente mantenha cópias de backup dos seus dados antes de executar migrações.

— Dado que o DatiaFlow opera sob a arquitetura Zero Data Retention, os dados dos clientes não são armazenados nos servidores da Varanext e não podem ser recuperados pela Varanext em caso de perda.`
      },
      {
        heading: '9. Suspensão, cancelamento e eliminação de dados',
        body: `a) Cancelamento pelo cliente: o cliente pode cancelar sua assinatura a qualquer momento contatando support@varanext.com. Após o cancelamento:
— Os dados de configuração (pipelines, mapeamentos) são conservados durante 30 dias para permitir a reativação.
— Passados os 30 dias, os dados de configuração são eliminados permanentemente.
— Os dados processados em trânsito não são afetados (ZDR — não há dados armazenados).

b) Suspensão pela Varanext: a Varanext pode suspender ou cancelar o acesso do cliente em caso de:
— Violação destes Termos de Serviço.
— Falta de pagamento (quando aplicável).
— Atividade suspeita, fraudulenta ou que comprometa a segurança da plataforma.
— Requisição de autoridade competente.

Em caso de suspensão, a Varanext notificará o cliente por e-mail com o motivo da suspensão, exceto em casos de urgência por segurança.

c) Solicitação de eliminação de dados (Direito ao Esquecimento): o cliente pode solicitar a eliminação completa da sua conta e de todos os dados associados a qualquer momento, independentemente de desejar cancelar sua assinatura. O processo é o seguinte:
— O usuário solicita a eliminação pela plataforma ou enviando um e-mail para support@varanext.com.
— Um e-mail de confirmação é enviado com um link de verificação válido por 48 horas.
— Após a confirmação, inicia-se um período de carência de 30 dias durante o qual a solicitação pode ser cancelada.
— Transcorrido o período de carência, a eliminação é irreversível. Os dados do cliente são anonimizados para cumprir as obrigações de accountability da Varanext.
— Para mais informações sobre o processo e os dados afetados, consulte nossa Política de Privacidade.`
      },
      {
        heading: '10. Indenização',
        body: `O cliente compromete-se a indenizar e manter indene a Varanext, seus diretores, funcionários e contratados de qualquer reclamação, demanda, responsabilidade, dano, custo ou despesa (incluindo honorários advocatícios razoáveis) decorrentes de:

— O uso do serviço pelo cliente em violação destes termos.
— A violação de direitos de terceiros pelo cliente através do uso do serviço.
— Os dados fornecidos ou processados pelo cliente através da plataforma.`
      },
      {
        heading: '11. Alterações nestes termos',
        body: `A Varanext reserva-se o direito de modificar estes Termos de Serviço. Em caso de mudanças substanciais, notificaremos você por e-mail com pelo menos 30 dias de antecedência. O uso continuado do serviço após a entrada em vigor das mudanças constituirá a aceitação dos novos termos.

Se você não concordar com as mudanças, pode cancelar sua assinatura antes que entrem em vigor.`
      },
      {
        heading: '12. Lei aplicável e jurisdição',
        body: `Estes Termos de Serviço são regidos pelas leis da República do Peru.

Qualquer controvérsia derivada destes termos ou do uso do serviço será submetida à jurisdição exclusiva dos tribunais do distrito judicial de Lima, Peru, renunciando as partes a qualquer outro foro que lhes pudesse corresponder.

Para usuários da União Europeia: as disposições obrigatórias de proteção ao consumidor do seu país de residência permanecerão aplicáveis na medida em que a lei o exija.`
      },
      {
        heading: '13. Divisibilidade',
        body: `Se alguma disposição destes Termos de Serviço for declarada inválida ou inexequível por um tribunal competente, as disposições restantes permanecerão em pleno vigor e efeito.`
      },
      {
        heading: '14. Contato',
        body: `Para qualquer consulta relacionada a estes Termos de Serviço, entre em contato:

Varanext
Lima, Peru
support@varanext.com`
      }
    ],
    privacyPolicyLink: 'Política de Privacidade',
    cookiePolicyLink: 'Política de Cookies'
  }
};

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
export const TermsOfService = () => {
  const { language } = useLanguageStore();
  React.useEffect(() => { window.scrollTo(0, 0); }, []);
  const lang = ['ES', 'EN', 'PT'].includes(language?.toUpperCase()) ? language.toUpperCase() : 'ES';
  const c = CONTENT[lang];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex-shrink-0">
            <img src={logoHorizontal} alt="DatiaFlow" className="hidden sm:block h-11 w-auto" />
            <img src={logoSquare} alt="DatiaFlow" className="sm:hidden h-11 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-blue-600 transition-colors flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" />
            {c.backToHome}
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl sm:text-3xl font-black text-slate-800">{c.title}</h1>
        </div>
        <p className="text-sm text-slate-400 mb-10">{c.lastUpdated}</p>

        <div className="space-y-8">
          {c.sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-lg font-bold text-slate-800 mb-3">{section.heading}</h2>
              <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                {section.body}
              </div>
            </section>
          ))}
        </div>

        {/* Footer links */}
        <div className="mt-16 pt-8 border-t border-slate-100 flex flex-wrap gap-6 text-sm">
          <Link to="/privacy" className="text-blue-600 hover:underline">{c.privacyPolicyLink}</Link>
          <Link to="/cookies" className="text-blue-600 hover:underline">{c.cookiePolicyLink}</Link>
          <span className="text-slate-500 hover:text-blue-600 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" />
            <SafeEmail u="support" d="varanext.com" />
          </span>
        </div>
      </main>
    </div>
  );
};
